"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { ExternalLink } from "lucide-react";

const cormorant = {
    className: "font-cormorant font-serif",
};

export interface GuestiqoHeroBlockProps {
    tag?: string;
    title?: string;
    italicQuote?: string;
    description?: string;
    primaryBtnText?: string;
    primaryBtnLink?: string;
    secondaryBtnText?: string;
    secondaryBtnLink?: string;
    imageSrc?: string;
    imageAlt?: string;
    floatingLabel?: string;
}

export default function GuestiqoHeroBlock({
    tag = "// Product & Collaboration",
    title = "Guestiqo",
    italicQuote = '"A full-spectrum hospitality platform designed, engineered, and shipped by Kitebe to transform physical stays into elegant digital experiences."',
    description = "From brand identity through production infrastructure, we built the entire multi-tenant digital ecosystem powering contactless guest support across the UK short-term rental market.",
    primaryBtnText = "Visit Website",
    primaryBtnLink = "https://guestiqo.uk/",
    secondaryBtnText = "Try Guest Demo",
    secondaryBtnLink = "https://demo.guestiqo.uk/",
    imageSrc = "/images/guestiqo_app_screenshot.png",
    imageAlt = "Guestiqo guest app home screen",
    floatingLabel = "UK Hospitality Ledger"
}: GuestiqoHeroBlockProps) {
    return (
        <section className="relative min-h-[90vh] pt-32 pb-20 px-6 md:px-12 lg:px-24 xl:px-32 flex items-center bg-[#FDFBF7] w-full" data-header-theme="dark">
            {/* Background Texture Overlay */}
            <div className="absolute inset-0 bg-[url('/images/ui-ux/glasstexture.png')] opacity-[0.03] pointer-events-none" />
            
            <div className="container mx-auto z-10 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    
                    {/* Left Column: Typography Showcase */}
                    <div className="lg:col-span-7 flex flex-col justify-center">
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }} 
                            animate={{ opacity: 1, y: 0 }} 
                            transition={{ duration: 0.8 }}
                            className="space-y-6"
                        >
                            {tag && (
                                <div className="inline-flex items-center gap-2 border-b border-[#1C1C1C]/20 pb-2">
                                    <span className="text-xs font-mono uppercase tracking-widest text-[#8C8276]">{tag}</span>
                                </div>
                            )}
                            
                            <h1 className={`${cormorant.className} text-6xl md:text-8xl xl:text-9xl font-light tracking-tight text-[#1C1C1C] leading-[0.85]`}>
                                {title}
                            </h1>
                            
                            {italicQuote && (
                                <p className={`${cormorant.className} text-2xl md:text-3xl text-[#4E4E4E] italic max-w-xl leading-relaxed pt-4`}>
                                    {italicQuote}
                                </p>
                            )}

                            {description && (
                                <div className="pt-6">
                                    <p className="text-sm text-[#8C8276] max-w-md leading-relaxed font-light font-sans">
                                        {description}
                                    </p>
                                </div>
                            )}

                            <div className="pt-4 flex flex-wrap gap-3">
                                {primaryBtnText && primaryBtnLink && (
                                    <a href={primaryBtnLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1C1C1C] text-[#FDFBF7] hover:bg-[#2E2C29] transition-all duration-300 font-mono text-[11px] uppercase tracking-wider font-semibold rounded-none w-fit">
                                        {primaryBtnText} <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                )}
                                {secondaryBtnText && secondaryBtnLink && (
                                    <a href={secondaryBtnLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#1C1C1C]/20 text-[#1C1C1C] hover:border-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-[#FDFBF7] transition-all duration-300 font-mono text-[11px] uppercase tracking-wider font-semibold rounded-none w-fit">
                                        {secondaryBtnText}
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column: Device rendered around the source app screenshot */}
                    <div className="lg:col-span-5 w-full flex justify-center items-center py-4 lg:py-0">
                        <motion.div 
                            initial={{ opacity: 0, y: 30, rotate: -2 }}
                            animate={{ opacity: 1, y: 0, rotate: 0 }}
                            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                            className="relative w-full max-w-[520px]"
                        >
                            {/* Keeping the screenshot separate from the frame prevents baked-in cropping. */}
                            {imageSrc && (
                                <div className="relative mx-auto w-fit rotate-[3deg]">
                                    <div className="absolute -left-[3px] top-24 h-9 w-[3px] rounded-l-sm bg-[#29292d]" />
                                    <div className="absolute -left-[3px] top-36 h-14 w-[3px] rounded-l-sm bg-[#29292d]" />
                                    <div className="absolute -right-[3px] top-32 h-20 w-[3px] rounded-r-sm bg-[#1d1d20]" />

                                    <div className="w-[min(70vw,330px)] rounded-[3.6rem] bg-gradient-to-br from-[#4a4a50] via-[#0b0b0d] to-[#303035] p-[7px] shadow-[0_42px_85px_-30px_rgba(28,28,28,0.45)]">
                                        <div className="rounded-[3.2rem] bg-[#09090a] p-[5px]">
                                            <div className="relative aspect-[390/844] overflow-hidden rounded-[2.85rem] bg-[#F5F2ED]">
                                                <Image
                                                    src={imageSrc}
                                                    alt={imageAlt}
                                                    fill
                                                    sizes="(max-width: 768px) 70vw, 330px"
                                                    className="object-cover object-top origin-left scale-x-[1.025]"
                                                    priority
                                                />
                                                <div className="absolute left-1/2 top-3 z-20 h-6 w-20 -translate-x-1/2 rounded-full bg-[#080809] shadow-sm" />
                                                <div className="absolute inset-0 z-10 rounded-[2.85rem] bg-gradient-to-tr from-transparent via-white/[0.025] to-white/[0.09] pointer-events-none" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Floating label chip — bottom right */}
                            {floatingLabel && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 1 }}
                                    className="absolute bottom-6 right-0 lg:-right-2 z-30 font-mono text-[9px] uppercase tracking-widest bg-[#1C1C1C]/85 text-white px-4 py-2 backdrop-blur-sm hidden md:block"
                                >
                                    {floatingLabel}
                                </motion.div>
                            )}
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}
